<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  event: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const { accountScopedUrl } = useAccount();

const activeTab = ref('summary');

watch(
  () => props.event?.id,
  () => {
    activeTab.value = 'summary';
  }
);

const formatDate = value => {
  if (!value) return t('AUTOMATION_EVENTS.EMPTY_VALUE');

  return new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const payloadJson = payload => JSON.stringify(payload || {}, null, 2);
const hasOwn = (record, key) =>
  Object.prototype.hasOwnProperty.call(record || {}, key);
const hasEventPayloads = computed(
  () =>
    hasOwn(props.event, 'raw_payload') ||
    hasOwn(props.event, 'normalized_payload')
);
const hasStepPayloads = step =>
  hasOwn(step, 'request_payload') || hasOwn(step, 'response_payload');

const statusLabel = status => {
  switch (status) {
    case 'completed':
      return t('AUTOMATION_EVENTS.STATUSES.COMPLETED');
    case 'failed':
      return t('AUTOMATION_EVENTS.STATUSES.FAILED');
    case 'ignored':
      return t('AUTOMATION_EVENTS.STATUSES.IGNORED');
    case 'manual_action':
      return t('AUTOMATION_EVENTS.STATUSES.MANUAL_ACTION');
    case 'pending':
      return t('AUTOMATION_EVENTS.STATUSES.PENDING');
    case 'processing':
      return t('AUTOMATION_EVENTS.STATUSES.PROCESSING');
    case 'received':
      return t('AUTOMATION_EVENTS.STATUSES.RECEIVED');
    case 'running':
      return t('AUTOMATION_EVENTS.STATUSES.RUNNING');
    case 'skipped':
      return t('AUTOMATION_EVENTS.STATUSES.SKIPPED');
    case 'success':
      return t('AUTOMATION_EVENTS.STATUSES.SUCCESS');
    default:
      return status;
  }
};

const statusClass = status => {
  const classes = {
    completed: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    manual_action: 'bg-n-amber-3 text-n-amber-11',
    processing: 'bg-n-blue-3 text-n-blue-11',
    ignored: 'bg-n-slate-3 text-n-slate-11',
    received: 'bg-n-slate-3 text-n-slate-11',
    success: 'bg-n-teal-3 text-n-teal-11',
    running: 'bg-n-amber-3 text-n-amber-11',
    pending: 'bg-n-slate-3 text-n-slate-11',
    skipped: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[status] || classes.received;
};

const eventSubtitle = computed(() => {
  if (!props.event) return '';

  return `#${props.event.id} · ${props.event.source} · ${props.event.event_type}`;
});

const stepMeta = step =>
  t('AUTOMATION_EVENTS.DETAIL.STEP_META', {
    stepKey: step.step_key,
    attempts: step.attempt_count,
  });

const contactUrl = computed(() => {
  if (!props.event?.contact_id) return null;
  return accountScopedUrl(`contacts/${props.event.contact_id}`);
});

const conversationUrl = computed(() => {
  const displayId = props.event?.conversation_display_id;
  if (!displayId) return null;
  return accountScopedUrl(`conversations/${displayId}`);
});

const summaryRows = computed(() => {
  if (!props.event) return [];

  return [
    [t('AUTOMATION_EVENTS.FIELDS.SOURCE'), props.event.source],
    [t('AUTOMATION_EVENTS.FIELDS.EVENT_TYPE'), props.event.event_type],
    [t('AUTOMATION_EVENTS.FIELDS.LAUNCH'), props.event.launch_id],
    [t('AUTOMATION_EVENTS.FIELDS.STATUS'), statusLabel(props.event.status)],
    [t('AUTOMATION_EVENTS.FIELDS.TRANSACTION'), props.event.transaction_id],
    [
      t('AUTOMATION_EVENTS.FIELDS.PRODUCT'),
      props.event.product_name || props.event.product_code,
    ],
    [t('AUTOMATION_EVENTS.FIELDS.OFFER'), props.event.offer_code],
    [t('AUTOMATION_EVENTS.FIELDS.CUSTOMER'), props.event.customer_name],
    [t('AUTOMATION_EVENTS.FIELDS.EMAIL'), props.event.customer_email],
    [t('AUTOMATION_EVENTS.FIELDS.PHONE'), props.event.customer_phone],
    [
      t('AUTOMATION_EVENTS.FIELDS.EXTERNAL_EVENT_ID'),
      props.event.external_event_id,
    ],
    [
      t('AUTOMATION_EVENTS.FIELDS.IDEMPOTENCY_KEY'),
      props.event.idempotency_key,
    ],
    [
      t('AUTOMATION_EVENTS.FIELDS.RECEIVED_AT'),
      formatDate(props.event.received_at),
    ],
    [
      t('AUTOMATION_EVENTS.FIELDS.PROCESSED_AT'),
      formatDate(props.event.processed_at),
    ],
  ].filter(([, value]) => value);
});

const tabs = computed(() => [
  { id: 'summary', label: t('AUTOMATION_EVENTS.DETAIL.TABS.SUMMARY') },
  { id: 'steps', label: t('AUTOMATION_EVENTS.DETAIL.TABS.STEPS') },
  { id: 'technical', label: t('AUTOMATION_EVENTS.DETAIL.TABS.TECHNICAL') },
]);
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col bg-n-solid-1">
    <header
      class="flex shrink-0 items-start justify-between gap-3 border-b border-n-weak px-4 py-3"
    >
      <div class="min-w-0">
        <h2 class="truncate text-base font-semibold text-n-slate-12">
          {{ t('AUTOMATION_EVENTS.DETAIL.TITLE') }}
        </h2>
        <p v-if="event" class="mt-0.5 truncate text-xs text-n-slate-11">
          {{ eventSubtitle }}
        </p>
      </div>
      <button
        v-if="compact"
        type="button"
        class="flex size-8 shrink-0 items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
        :aria-label="t('AUTOMATION_EVENTS.ACTIONS.CLOSE')"
        @click="emit('close')"
      >
        <span class="i-ri-close-line size-4" />
      </button>
    </header>

    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center gap-2 text-sm text-n-slate-11"
    >
      <span class="i-ri-loader-2-line size-4 animate-spin" />
      {{ t('AUTOMATION_EVENTS.LOADING') }}
    </div>

    <div
      v-else-if="!event"
      class="flex flex-1 items-center justify-center px-6 text-center text-sm text-n-slate-11"
    >
      {{ t('AUTOMATION_EVENTS.DETAIL.EMPTY_SELECTION') }}
    </div>

    <template v-else>
      <div
        v-if="contactUrl || conversationUrl"
        class="flex shrink-0 flex-wrap gap-2 border-b border-n-weak px-4 py-2"
      >
        <a
          v-if="contactUrl"
          :href="contactUrl"
          class="inline-flex h-8 items-center gap-1.5 rounded-md bg-n-alpha-2 px-2.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-3"
        >
          <span class="i-ri-user-line size-3.5" />
          {{ t('AUTOMATION_EVENTS.ACTIONS.OPEN_CONTACT') }}
        </a>
        <a
          v-if="conversationUrl"
          :href="conversationUrl"
          class="inline-flex h-8 items-center gap-1.5 rounded-md bg-n-alpha-2 px-2.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-3"
        >
          <span class="i-ri-message-2-line size-3.5" />
          {{ t('AUTOMATION_EVENTS.ACTIONS.OPEN_CONVERSATION') }}
        </a>
      </div>

      <nav
        class="flex shrink-0 gap-1 border-b border-n-weak px-3 pt-2"
        role="tablist"
      >
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          role="tab"
          class="rounded-t-md px-3 py-2 text-xs font-medium transition-colors"
          :class="
            activeTab === tab.id
              ? 'border-b-2 border-n-brand text-n-brand'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          :aria-selected="activeTab === tab.id"
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </nav>

      <div class="min-h-0 flex-1 overflow-y-auto p-4">
        <section v-if="activeTab === 'summary'" class="space-y-3">
          <div v-if="event.status" class="flex items-center gap-2">
            <span
              class="rounded px-2 py-0.5 text-xs font-medium"
              :class="statusClass(event.status)"
            >
              {{ statusLabel(event.status) }}
            </span>
          </div>

          <div
            v-if="event.current_error"
            class="rounded-md border border-n-ruby-5 bg-n-ruby-2 px-3 py-2"
          >
            <div class="text-[11px] font-medium uppercase text-n-ruby-11">
              {{ t('AUTOMATION_EVENTS.FIELDS.CURRENT_ERROR') }}
            </div>
            <div class="mt-1 break-words text-sm text-n-ruby-12">
              {{ event.current_error }}
            </div>
          </div>

          <div class="grid gap-2 sm:grid-cols-2">
            <div
              v-for="[label, value] in summaryRows"
              :key="label"
              class="rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2"
            >
              <div class="text-[11px] font-medium uppercase text-n-slate-10">
                {{ label }}
              </div>
              <div class="mt-0.5 break-words text-sm text-n-slate-12">
                {{ value }}
              </div>
            </div>
          </div>
        </section>

        <section v-else-if="activeTab === 'steps'">
          <div
            v-if="!event.steps?.length"
            class="rounded-md border border-n-weak px-3 py-8 text-center text-sm text-n-slate-11"
          >
            {{ t('AUTOMATION_EVENTS.DETAIL.NO_STEPS') }}
          </div>
          <div
            v-else
            class="relative ml-2 space-y-2 border-l border-n-weak pl-4"
          >
            <div
              v-for="step in event.steps"
              :key="step.id"
              class="relative rounded-md border border-n-weak bg-n-solid-2 px-3 py-3"
            >
              <span
                class="i-ri-git-branch-line absolute -left-[1.35rem] top-3 size-4 bg-n-solid-1 text-n-slate-10"
              />
              <div class="flex flex-wrap items-start justify-between gap-2">
                <div class="min-w-0">
                  <div class="truncate text-sm font-medium text-n-slate-12">
                    {{ step.step_label || step.step_key }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ stepMeta(step) }}
                  </div>
                </div>
                <span
                  class="rounded px-2 py-0.5 text-xs font-medium"
                  :class="statusClass(step.status)"
                >
                  {{ statusLabel(step.status) }}
                </span>
              </div>
              <p
                v-if="step.error_message"
                class="mt-2 break-words text-xs text-n-ruby-11"
              >
                {{ step.error_message }}
              </p>
              <div
                v-if="hasStepPayloads(step)"
                class="mt-2 grid gap-2 md:grid-cols-2"
              >
                <textarea
                  readonly
                  class="h-40 resize-none overflow-auto rounded bg-n-alpha-2 p-2 font-mono text-[11px] text-n-slate-11"
                  :value="payloadJson(step.request_payload)"
                />
                <textarea
                  readonly
                  class="h-40 resize-none overflow-auto rounded bg-n-alpha-2 p-2 font-mono text-[11px] text-n-slate-11"
                  :value="payloadJson(step.response_payload)"
                />
              </div>
            </div>
          </div>
        </section>

        <section v-else class="space-y-4">
          <template v-if="hasEventPayloads">
            <div>
              <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
                {{ t('AUTOMATION_EVENTS.DETAIL.RAW_PAYLOAD') }}
              </h3>
              <textarea
                readonly
                class="h-56 w-full resize-none overflow-auto rounded-md border border-n-weak bg-n-alpha-2 p-3 font-mono text-xs text-n-slate-11"
                :value="payloadJson(event.raw_payload)"
              />
            </div>
            <div>
              <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
                {{ t('AUTOMATION_EVENTS.DETAIL.NORMALIZED_PAYLOAD') }}
              </h3>
              <textarea
                readonly
                class="h-56 w-full resize-none overflow-auto rounded-md border border-n-weak bg-n-alpha-2 p-3 font-mono text-xs text-n-slate-11"
                :value="payloadJson(event.normalized_payload)"
              />
            </div>
          </template>
          <div
            v-else
            class="rounded-md border border-n-weak px-3 py-8 text-center text-sm text-n-slate-11"
          >
            {{
              event
                ? t('AUTOMATION_EVENTS.DETAIL.TECHNICAL_ADMIN_ONLY')
                : t('AUTOMATION_EVENTS.DETAIL.NO_TECHNICAL')
            }}
          </div>
        </section>
      </div>
    </template>
  </div>
</template>
